import { useCallback, useEffect, useRef, useState } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import { orgApi, contentApi, apiErrorMessage } from '../services/api';
import { withStorageAccess } from '../services/storageAccess';
import { hasOrgNotes } from '../services/notes';
import { useOrg, useReloadOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import { Button, Card, Field, PageSkeleton, ErrorState, PageHeader, ConfirmDialog } from '../components/ui';
import { resolveFeatures, EDITABLE_FEATURES } from '../utils/features';
import { isSwitchingOffLearningCenter, learningCenterOffConfirmation } from '../utils/learningCenter';
import InstituteMark from '../components/InstituteMark';
import JoinCodeCard from '../components/JoinCodeCard';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
// No SVG: the student app draws the logo with Image.network, which cannot
// render SVG, so an SVG logo would silently show as initials there.
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// Institute settings: how the institute appears to students, the join code,
// and which parts of the student app are switched on.
export default function SettingsPage() {
  const org = useOrg();
  const reloadOrg = useReloadOrg();
  const toast = useToast();
  const fileInput = useRef(null);

  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [tagline, setTagline] = useState(org?.metadata?.tagline || '');
  const [uploading, setUploading] = useState(false);
  // A feature change waiting on the owner's confirmation: { key, label, nextValue, title, message, confirmLabel }.
  const [pendingToggle, setPendingToggle] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    orgApi.getOrgById(org.id)
      .then((res) => {
        setFeatures(resolveFeatures(res.data));
        setTagline(res.data?.metadata?.tagline || '');
        setError(null);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load settings')))
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key, label, nextValue) => {
    const previous = features;
    setSaving(key);
    setFeatures({ ...features, [key]: nextValue });
    try {
      const res = await orgApi.updateFeatures(org.id, { [key]: nextValue });
      setFeatures(resolveFeatures({ features: res.data }));
      toast.success(`${label} ${nextValue ? 'enabled' : 'disabled'} for your students.`);
    } catch (err) {
      setFeatures(previous);
      toast.error(apiErrorMessage(err, `Failed to update ${label}`));
    } finally {
      setSaving(null);
    }
  };

  // Switching the learning centre off hides everything the institute has
  // published from its students, so check for such material and confirm first.
  const requestToggle = async (key, label, nextValue) => {
    if (isSwitchingOffLearningCenter(key, nextValue)) {
      setSaving(key);
      const [published, notes] = await Promise.allSettled([
        contentApi.list(org.id),
        hasOrgNotes(org.id),
      ]);
      setSaving(null);
      const confirmation = learningCenterOffConfirmation({
        publishedCount: published.status === 'fulfilled' ? (published.value.data || []).length : null,
        hasCustomNotes: notes.status === 'fulfilled' ? notes.value : null,
      });
      if (confirmation) {
        setPendingToggle({ key, label, nextValue, ...confirmation });
        return;
      }
    }
    toggle(key, label, nextValue);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      toast.error('Use a PNG, JPG or WebP image.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Keep the logo under 2 MB.');
      return;
    }
    setUploading(true);
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      // Lives beside the institute's notes in Storage; the student app reads
      // the resulting URL from the organization's metadata.
      const target = storageRef(storage, `orgs/${org.id}/branding/logo-${Date.now()}.${ext}`);
      await withStorageAccess(org.id, () => uploadBytes(target, file, { contentType: file.type }));
      const logoUrl = await getDownloadURL(target);
      await orgApi.updateBranding(org.id, { logoUrl });
      await reloadOrg();
      toast.success('Logo updated. Students will see it the next time they open the app.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not upload the logo'));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const removeLogo = async () => {
    setUploading(true);
    try {
      await orgApi.updateBranding(org.id, { logoUrl: '' });
      await reloadOrg();
      toast.success('Logo removed.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not remove the logo'));
    } finally {
      setUploading(false);
    }
  };

  const saveTagline = async () => {
    setSaving('tagline');
    try {
      await orgApi.updateBranding(org.id, { tagline: tagline.trim() });
      await reloadOrg();
      toast.success('Tagline saved.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the tagline'));
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;

  return (
    <>
      <PageHeader
        eyebrow="Institute settings"
        title="Settings"
        subtitle="How your institute appears to students, and what they can use."
      />

      <Card className="settings-card">
        <div className="section-title">
          <div>
            <h2>Branding</h2>
            <p>Your logo and name appear inside the Prepgen app for your students.</p>
          </div>
        </div>
        <div className="logo-upload">
          <InstituteMark org={org} size={72} />
          <div>
            <b style={{ display: 'block', marginBottom: 6 }}>{org.name}</b>
            <div className="logo-upload-actions">
              <input
                ref={fileInput}
                type="file"
                accept={LOGO_TYPES.join(',')}
                onChange={(e) => uploadLogo(e.target.files?.[0])}
              />
              <Button variant="secondary" disabled={uploading} onClick={() => fileInput.current?.click()}>
                {uploading ? 'Uploading…' : org.metadata?.logoUrl ? 'Replace logo' : 'Upload logo'}
              </Button>
              {org.metadata?.logoUrl && (
                <Button variant="text" disabled={uploading} onClick={removeLogo}>Remove</Button>
              )}
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>Square works best. PNG, JPG or WebP, under 2 MB.</p>
          </div>
        </div>
        <div className="form">
          <Field label="Tagline (optional)" hint="Shown under your name in the console. Keep it short.">
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                maxLength={120}
                placeholder="e.g. Coaching for CAT since 2009"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
              />
              <Button variant="secondary" disabled={saving === 'tagline' || tagline === (org.metadata?.tagline || '')} onClick={saveTagline}>
                {saving === 'tagline' ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </Field>
        </div>
      </Card>

      <div className="settings-card">
        <JoinCodeCard code={org.code} />
      </div>

      <Card className="settings-card">
        <div className="section-title">
          <div>
            <h2>Student features</h2>
            <p>Assigned tests are always available. Everything else is your call.</p>
          </div>
        </div>
        <table>
          <thead><tr><th>Feature</th><th>Available to students</th></tr></thead>
          <tbody>
            <tr>
              <td>
                <b>Assigned tests</b>
                <small>The tests you schedule for a batch. Always on.</small>
              </td>
              <td><span className="status">Always on</span></td>
            </tr>
            {EDITABLE_FEATURES.map((feature) => (
              <tr key={feature.key}>
                <td>
                  <b>{feature.label}</b>
                  <small>{feature.description}</small>
                </td>
                <td>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(features?.[feature.key])}
                      disabled={saving === feature.key}
                      onChange={(e) => requestToggle(feature.key, feature.label, e.target.checked)}
                    />
                    {features?.[feature.key] ? 'On' : 'Off'}
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="helper" style={{ padding: '0 16px 16px' }}>Changes apply the next time a student opens the app.</p>
      </Card>

      {pendingToggle && (
        <ConfirmDialog
          title={pendingToggle.title}
          message={pendingToggle.message}
          confirmLabel={pendingToggle.confirmLabel}
          onCancel={() => setPendingToggle(null)}
          onConfirm={() => {
            const { key, label, nextValue } = pendingToggle;
            setPendingToggle(null);
            toggle(key, label, nextValue);
          }}
        />
      )}
    </>
  );
}
